import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { DollarSign, Loader2, Trash2, CheckCircle2, Plus, Wallet } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLES = {
  paid: "bg-green-100 text-green-700",
  deposit_paid: "bg-blue-100 text-blue-700",
  partial: "bg-amber-100 text-amber-700",
  unpaid: "bg-stone-100 text-stone-600",
  refunded: "bg-red-100 text-red-700",
};

const PAYMENT_METHODS = [
  { value: "venmo", label: "Venmo" },
  { value: "check", label: "Check" },
  { value: "cash", label: "Cash" },
  { value: "zelle", label: "Zelle" },
  { value: "cashapp", label: "Cash App" },
  { value: "paypal", label: "PayPal" },
  { value: "card", label: "Card" },
  { value: "other", label: "Other" },
];

export const PaymentsTab = ({ getAuthHeaders }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ expected: 0, collected: 0, outstanding: 0 });
  const [filter, setFilter] = useState("all"); // all, outstanding, paid
  const [selected, setSelected] = useState(null); // booking row for ledger
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ amount: "", method: "venmo", payment_date: "", reference: "", notes: "", send_receipt: false });

  const fetchOverview = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/payments/overview`, getAuthHeaders());
      setRows(res.data.rows || []);
      setTotals(res.data.totals || { expected: 0, collected: 0, outstanding: 0 });
    } catch (e) {
      toast.error("Failed to load payment overview");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    const t = setTimeout(() => { fetchOverview(); }, 0);
    return () => clearTimeout(t);
  }, [fetchOverview]);

  const openLedger = async (row) => {
    setSelected(row);
    setLedgerLoading(true);
    setLedger(null);
    setForm({ amount: "", method: row.preferred_payment_method || "venmo", payment_date: "", reference: "", notes: "", send_receipt: false });
    try {
      const res = await axios.get(`${API}/admin/bookings/${row.booking_id}/payments`, getAuthHeaders());
      setLedger(res.data);
    } catch (e) {
      toast.error("Failed to load payment ledger");
      setSelected(null);
    } finally {
      setLedgerLoading(false);
    }
  };

  const refreshLedger = async (bookingId) => {
    const res = await axios.get(`${API}/admin/bookings/${bookingId}/payments`, getAuthHeaders());
    setLedger(res.data);
    fetchOverview();
  };

  const handleRecord = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid payment amount");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/bookings/${selected.booking_id}/payments`,
        {
          amount,
          method: form.method,
          payment_date: form.payment_date || null,
          reference: form.reference || null,
          notes: form.notes || null,
          send_receipt: form.send_receipt,
        },
        getAuthHeaders()
      );
      toast.success(`Recorded ${money(amount)} payment${form.send_receipt ? " + receipt sent" : ""}`);
      setForm({ amount: "", method: form.method, payment_date: "", reference: "", notes: "", send_receipt: false });
      await refreshLedger(selected.booking_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record payment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVoid = async (paymentId) => {
    if (!window.confirm("Void this payment? The balance will be recalculated.")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${selected.booking_id}/payments/${paymentId}`, getAuthHeaders());
      toast.success("Payment voided");
      await refreshLedger(selected.booking_id);
    } catch (e) {
      toast.error("Failed to void payment");
    }
  };

  const handleMarkPaidFull = async () => {
    if (!window.confirm("Mark this reservation as Paid in Full? The remaining balance will be recorded as paid.")) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/bookings/${selected.booking_id}/mark-paid-full?method=${form.method}`, {}, getAuthHeaders());
      toast.success("Reservation marked Paid in Full");
      await refreshLedger(selected.booking_id);
    } catch (e) {
      toast.error("Failed to mark paid in full");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredRows = rows.filter((r) => {
    if (filter === "outstanding") return r.balance_due > 0 && r.status !== "cancelled";
    if (filter === "paid") return r.balance_due <= 0 && r.status !== "cancelled";
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="payments-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#134E4A]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payments-tab">
      <div className="flex items-center gap-3">
        <Wallet className="w-6 h-6 text-[#134E4A]" />
        <h2 className="text-2xl font-semibold text-[#1C1917]">Payment Tracking</h2>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-stone-200 p-5 rounded-lg" data-testid="payments-total-expected">
          <p className="text-xs uppercase tracking-wide text-[#78716C]">Total Expected</p>
          <p className="text-2xl font-semibold text-[#1C1917] mt-1">{money(totals.expected)}</p>
        </div>
        <div className="bg-white border border-stone-200 p-5 rounded-lg" data-testid="payments-total-collected">
          <p className="text-xs uppercase tracking-wide text-[#78716C]">Collected</p>
          <p className="text-2xl font-semibold text-green-700 mt-1">{money(totals.collected)}</p>
        </div>
        <div className="bg-white border border-stone-200 p-5 rounded-lg" data-testid="payments-total-outstanding">
          <p className="text-xs uppercase tracking-wide text-[#78716C]">Outstanding</p>
          <p className="text-2xl font-semibold text-amber-700 mt-1">{money(totals.outstanding)}</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        {["all", "outstanding", "paid"].map((f) => (
          <button
            key={f}
            data-testid={`payments-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
              filter === f ? "bg-[#134E4A] text-white border-[#134E4A]" : "bg-white text-[#78716C] border-stone-200 hover:bg-stone-50"
            }`}
          >
            {f === "all" ? "All" : f === "outstanding" ? "Balance Due" : "Paid in Full"}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Guest</TableHead>
              <TableHead>Dates</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead>Next Due</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center text-[#78716C] py-8">No bookings</TableCell></TableRow>
            ) : filteredRows.map((r) => (
              <TableRow key={r.booking_id} data-testid={`payment-row-${r.booking_id}`} className={r.status === "cancelled" ? "opacity-50" : ""}>
                <TableCell className="font-medium">{r.guest_name}</TableCell>
                <TableCell className="text-sm text-[#78716C]">{r.check_in} → {r.check_out}</TableCell>
                <TableCell className="text-right">{money(r.total_price)}</TableCell>
                <TableCell className="text-right text-green-700">{money(r.amount_paid)}</TableCell>
                <TableCell className={`text-right font-medium ${r.balance_due > 0 ? "text-amber-700" : "text-green-700"}`}>{money(r.balance_due)}</TableCell>
                <TableCell className="text-sm">
                  {r.balance_due > 0 && r.next_due_label ? (
                    <span>{r.next_due_label}{r.next_due_date ? ` · ${r.next_due_date}` : ""}</span>
                  ) : <span className="text-green-700">—</span>}
                </TableCell>
                <TableCell>
                  <span className={`px-2 py-1 text-xs rounded ${STATUS_STYLES[r.payment_status] || "bg-stone-100 text-stone-600"}`}>
                    {r.status === "cancelled" ? "cancelled" : r.payment_status}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" data-testid={`manage-payments-${r.booking_id}`} onClick={() => openLedger(r)}>
                    <DollarSign className="w-3 h-3 mr-1" /> Manage
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Ledger dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setLedger(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="payment-ledger-dialog">
          <DialogHeader>
            <DialogTitle>Payment Ledger — {selected?.guest_name}</DialogTitle>
            <DialogDescription>
              {selected?.check_in} → {selected?.check_out}
            </DialogDescription>
          </DialogHeader>

          {ledgerLoading || !ledger ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#134E4A]" /></div>
          ) : (
            <div className="space-y-5">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-stone-50 rounded p-3">
                  <p className="text-xs text-[#78716C]">Trip Total</p>
                  <p className="text-lg font-semibold">{money(ledger.summary.total)}</p>
                </div>
                <div className="bg-green-50 rounded p-3">
                  <p className="text-xs text-[#78716C]">Paid</p>
                  <p className="text-lg font-semibold text-green-700" data-testid="ledger-paid">{money(ledger.summary.amount_paid)}</p>
                </div>
                <div className="bg-amber-50 rounded p-3">
                  <p className="text-xs text-[#78716C]">Balance Due</p>
                  <p className="text-lg font-semibold text-amber-700" data-testid="ledger-balance">{money(ledger.summary.balance_due)}</p>
                </div>
              </div>

              {/* Scheduled installments */}
              {ledger.schedule?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-[#1C1917] mb-2">Scheduled Installments</p>
                  <div className="space-y-1">
                    {ledger.schedule.map((s, i) => (
                      <div key={i} className="flex items-center justify-between text-sm border-b border-stone-100 py-1.5">
                        <span className="flex items-center gap-2">
                          {s.paid ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <span className="w-4 h-4 inline-block rounded-full border border-stone-300" />}
                          {s.label}{s.due ? <span className="text-[#78716C]"> · due {s.due}</span> : ""}
                        </span>
                        <span className={s.paid ? "text-green-700" : "text-[#78716C]"}>{money(s.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Payments received */}
              <div>
                <p className="text-sm font-medium text-[#1C1917] mb-2">Payments Received</p>
                {ledger.prior_amount > 0 && (
                  <div className="flex items-center justify-between text-sm bg-blue-50 rounded px-3 py-2 mb-2">
                    <span>Online / prior payments</span>
                    <span className="font-medium">{money(ledger.prior_amount)}</span>
                  </div>
                )}
                {ledger.payments.length === 0 && ledger.prior_amount <= 0 ? (
                  <p className="text-sm text-[#78716C]">No payments recorded yet.</p>
                ) : (
                  <div className="space-y-1">
                    {ledger.payments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between text-sm border border-stone-100 rounded px-3 py-2" data-testid={`ledger-payment-${p.id}`}>
                        <div>
                          <span className="font-medium">{money(p.amount)}</span>
                          <span className="text-[#78716C]"> · {p.method}{p.payment_date ? ` · ${p.payment_date}` : ""}</span>
                          {p.reference && <span className="text-[#78716C]"> · ref: {p.reference}</span>}
                          {p.notes && <div className="text-xs text-[#78716C]">{p.notes}</div>}
                        </div>
                        <button onClick={() => handleVoid(p.id)} className="text-red-500 hover:text-red-700" title="Void payment" data-testid={`void-payment-${p.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Record a payment */}
              <div className="border-t border-stone-200 pt-4">
                <p className="text-sm font-medium text-[#1C1917] mb-3">Record a Payment</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Amount</Label>
                    <Input type="number" min="0" step="0.01" placeholder="0.00" value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Method</Label>
                    <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                      <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PAYMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Date</Label>
                    <Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} data-testid="payment-date-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Reference / Check #</Label>
                    <Input placeholder="optional" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="payment-reference-input" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Notes</Label>
                    <Textarea rows={2} placeholder="optional" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="payment-notes-input" />
                  </div>
                  <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.send_receipt} onChange={(e) => setForm({ ...form, send_receipt: e.target.checked })} data-testid="payment-send-receipt" />
                    Email the guest a payment receipt
                  </label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {ledger && ledger.summary.balance_due > 0 && (
              <Button variant="outline" onClick={handleMarkPaidFull} disabled={submitting} data-testid="mark-paid-full-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Mark Paid in Full
              </Button>
            )}
            <Button onClick={handleRecord} disabled={submitting || !ledger} className="bg-[#134E4A] hover:bg-[#0f3d3a]" data-testid="record-payment-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
